// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useEffect, useRef } from 'react';
import * as TowUtils from '../../utils/game/TowServiceUtils';
import RandomEventManager from '../../utils/game/RandomEventManager';
import EventBus, { EVENT_TYPES } from '../../utils/system/EventBus';

/**
 * Custom hook for managing all event listeners
 * Extracted from NavigationPanel to reduce bundle size
 */
export const useEventHandlers = ({
  // State setters
  setActiveEvent,
  setInCombat,
  setTowOfferData,
  setTowDestination,
  setShowTowAnimation,
  
  // State values
  showWarpEffect,
  inCombat,
  selectedSystem,
  warpTravelTime,
  warpTravelProgress,
  currentLocation,
  starSystems,
  distancePositions,
  towOfferData,
  fuelDepleted,
  
  // Functions
  startWarpTimer,
  warpTimerRef
}) => {
  const activeEventRef = useRef(null);

  // Keep activeEventRef in sync with activeEvent for warp timer closure
  useEffect(() => {
    const handleActiveEventChange = (data) => {
      activeEventRef.current = data;
    };

    const unsub = EventBus.on(EVENT_TYPES.ACTIVE_EVENT_CHANGED, handleActiveEventChange);
    return unsub;
  }, []);

  // Handle combat interruption during warp
  useEffect(() => {
    const handleCombatInterrupt = (data) => {
      if (data) {
        
        // Dispatch the combatEncounter event that WeaponsPanel listens for
        EventBus.emit(EVENT_TYPES.COMBAT_ENCOUNTER);
      }
    };

    const unsub = EventBus.on(EVENT_TYPES.COMBAT_INTERRUPT, handleCombatInterrupt);
    return unsub;
  }, []);

  // Listen for tow service acceptance from EnhancedWarpEventManager
  useEffect(() => {
    const handleAcceptTowService = () => {
      
      // If we have a selected system (destination), use that as reference point
      // Otherwise use current location
      const referencePos = selectedSystem 
        ? distancePositions[selectedSystem.id] 
        : currentLocation;
      
      
      // Find nearest star system with refuel
      const nearestStar = starSystems.reduce((nearest, system) => {
        if (!system.services || !system.services.includes('refuel')) return nearest;
        if (!nearest) return system;
        
        const systemPos = distancePositions[system.id];
        if (!systemPos) return nearest;
        
        const nearestPos = distancePositions[nearest.id];
        if (!nearestPos) return system;
        
        const nearestDistance = Math.sqrt(
          Math.pow((referencePos.x || 0) - nearestPos.x, 2) +
          Math.pow((referencePos.y || 0) - nearestPos.y, 2) +
          Math.pow((referencePos.z || 0) - nearestPos.z, 2)
        );
        const systemDistance = Math.sqrt(
          Math.pow((referencePos.x || 0) - systemPos.x, 2) +
          Math.pow((referencePos.y || 0) - systemPos.y, 2) +
          Math.pow((referencePos.z || 0) - systemPos.z, 2)
        );
        return systemDistance < nearestDistance ? system : nearest;
      }, null);
      
      if (nearestStar) {
        
        // Set up tow offer data that handleTowComplete expects
        setTowOfferData({
          nearestStar: nearestStar
        });
        
        setTowDestination(nearestStar.name);
        setShowTowAnimation(true);
      } else {
        console.error('[EventHandlers] No nearest star found!');
      }
    };

    const unsubTow = EventBus.on(EVENT_TYPES.ACCEPT_TOW_SERVICE, handleAcceptTowService);
    return unsubTow;
  }, [currentLocation, selectedSystem, starSystems, distancePositions, setTowOfferData, setTowDestination, setShowTowAnimation]);

  // Listen for enhanced event system resume notifications
  useEffect(() => {
    const handleEventSystemResume = () => {
      
      // Resume warp travel if we were in the middle of it
      if (showWarpEffect && warpTimerRef.current) {
        // Warp travel will continue from where it left off
        // RandomEventManager will also resume generating events
      }
    };

    const unsubResume = EventBus.on(EVENT_TYPES.EVENT_SYSTEM_RESUME, handleEventSystemResume);

    return unsubResume;
  }, [showWarpEffect, warpTimerRef]);

  // Listen for combatComplete to resume warp travel
  useEffect(() => {
    const handleCombatComplete = () => {
      
      // Resume warp if we were traveling
      if (selectedSystem && warpTravelTime > 0 && warpTravelProgress < 100) {
        // Restart the warp timer with remaining time
        const remainingTime = warpTravelTime * (1 - warpTravelProgress / 100);
        startWarpTimer(remainingTime);
      }
      
      // Notify enhanced event system that external activity is complete
      if (window.EnhancedWarpEventManager) {
        window.EnhancedWarpEventManager.notifyWarpResume();
      }
    };

    const handleCombatActive = () => {
      setInCombat(true);
    };

    const handleCombatInactive = () => {
      setInCombat(false);
    };

    const unsubComplete = EventBus.on(EVENT_TYPES.COMBAT_COMPLETE, handleCombatComplete);
    const unsubActive = EventBus.on(EVENT_TYPES.COMBAT_ACTIVE, handleCombatActive);
    const unsubInactive = EventBus.on(EVENT_TYPES.COMBAT_INACTIVE, handleCombatInactive);

    return () => {
      unsubComplete();
      unsubActive();
      unsubInactive();
    };
  }, [selectedSystem, warpTravelTime, warpTravelProgress, startWarpTimer, setInCombat]);

  // Listen for warp travel pause/resume events from mini-games
  useEffect(() => {
    // Listen for warp events from EnhancedWarpEventManager
    const handleWarpEventGenerated = (data) => {
      setActiveEvent(data.event);
      EventBus.emit(EVENT_TYPES.ACTIVE_EVENT_CHANGED, data.event);
    };

    const handleWarpEventCleared = () => {
      setActiveEvent(null);
      // Dispatch custom event to notify activeEventRef
      EventBus.emit(EVENT_TYPES.ACTIVE_EVENT_CHANGED, null);
    };

    const unsubGenerated = EventBus.on(EVENT_TYPES.WARP_EVENT_GENERATED, handleWarpEventGenerated);
    const unsubCleared = EventBus.on(EVENT_TYPES.WARP_EVENT_CLEARED, handleWarpEventCleared);

    return () => {
      unsubGenerated();
      unsubCleared();
    };
  }, [setActiveEvent]);

  // Simple event management - start/stop based on warp state
  useEffect(() => {
    if (showWarpEffect && !inCombat) {
      RandomEventManager.start();
    } else {
      RandomEventManager.stop();
    }
  }, [showWarpEffect, inCombat]);

  // Function to re-trigger tow offer when clicking stranded marker
  const handleStrandedClick = () => {
    if (!towOfferData || !fuelDepleted) return;
    
    const { nearestStar } = towOfferData;
    
    // Build tow actions using utility
    const actions = TowUtils.buildTowServiceActions(
      nearestStar.name,
      // Accept tow callback
      () => {
        setTowDestination(nearestStar.name);
        setShowTowAnimation(true);
      },
      // Decline tow callback
      () => {
        setActiveEvent(null);
      }
    );
    
    // Create stranded event using utility
    const strandedEvent = TowUtils.createStrandedEvent(towOfferData, actions);
    setActiveEvent(strandedEvent);
  };

  return {
    handleStrandedClick,
    activeEventRef
  };
};

export default useEventHandlers;